import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import makeStyles from '@material-ui/core/styles/makeStyles';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import ConfirmationDialog from '@tracker/shared/src/ConfirmationDialog';
import classNames from 'classnames';
import React, {useCallback, useState} from 'react';
import {Draggable, Droppable} from 'react-beautiful-dnd';
import {BookmarkLinkGroupWithId, BookmarkLinkWithId} from './Home';
import LinkFormDialog from './LinkFormDialog';
import LinkGroupFormDialog from './LinkGroupFormDialog';
import LinkListItem from './LinkListItem';

const useStyles = makeStyles(() => ({
    draggable: {
        cursor: 'grab',
    },
    dragging: {
        cursor: 'grabbing',
    },
}));

type Props = {
    index : number;
    linkGroup : BookmarkLinkGroupWithId;
    onUpdate : (linkGroup : BookmarkLinkGroupWithId) => Promise<void>;
    onDelete : (linkGroup : BookmarkLinkGroupWithId) => Promise<void>;
    className ?: string;
}

const LinkGroupCard : React.FC<Props> = ({className, index, linkGroup, onUpdate, onDelete} : Props) => {
    const classes = useStyles();
    const [optionsElement, setOptionsElement] = useState<HTMLElement | null>(null);
    const [createLinkDialogOpen, setCreateLinkDialogOpen] = useState(false);
    const [updateLinkGroupDialogOpen, setUpdateLinkGroupDialogOpen] = useState(false);
    const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);

    const handleCreateLink = useCallback(async (link : BookmarkLinkWithId) => onUpdate({
        ...linkGroup,
        links: [
            ...linkGroup.links,
            link,
        ],
    }), [onUpdate, linkGroup]);

    const handleUpdateLink = useCallback(async (link : BookmarkLinkWithId) => onUpdate({
        ...linkGroup,
        links: linkGroup.links.map(currentLink => currentLink.id === link.id ? link : currentLink),
    }), [onUpdate, linkGroup]);

    const handleDeleteLink  = useCallback(async (link : BookmarkLinkWithId) => onUpdate({
        ...linkGroup,
        links: linkGroup.links.filter(currentLink => currentLink.id !== link.id),
    }), [onUpdate, linkGroup]);

    const handleUpdateLinkGroup = useCallback(
        async (linkGroup : BookmarkLinkGroupWithId) => onUpdate(linkGroup),
        [onUpdate]
    );

    return (
        <>
            <Draggable draggableId={`group-${linkGroup.id}`} index={index}>
                {(provided, snapshot) => (
                    <Card
                        className={classNames(
                            className,
                            snapshot.isDragging ? classes.dragging : classes.draggable,
                        )}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{...provided.draggableProps.style}}
                    >
                        <CardHeader
                            title={linkGroup.name}
                            action={(
                                <>
                                    <IconButton onClick={event => setOptionsElement(event.currentTarget)}>
                                        <MoreVertIcon/>
                                    </IconButton>
                                    <Menu
                                        anchorEl={optionsElement}
                                        open={Boolean(optionsElement)}
                                        getContentAnchorEl={null}
                                        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                                        transformOrigin={{vertical: 'top', horizontal: 'right'}}
                                        onClose={() => setOptionsElement(null)}
                                    >
                                        <MenuItem onClick={() => {
                                            setUpdateLinkGroupDialogOpen(true);
                                            setOptionsElement(null);
                                        }}>Rename</MenuItem>
                                        <MenuItem onClick={() => {
                                            setConfirmDeleteDialogOpen(true);
                                            setOptionsElement(null);
                                        }}>Delete</MenuItem>
                                        <MenuItem onClick={() => {
                                            setCreateLinkDialogOpen(true);
                                            setOptionsElement(null);
                                        }}>Add Link</MenuItem>
                                    </Menu>
                                </>
                            )}
                        />
                        <Droppable droppableId={`group-${linkGroup.id}`} type="links">
                            {(provided, snapshot) => (
                                <List ref={provided.innerRef}>
                                    {linkGroup.links.map((link, index) => (
                                        <LinkListItem
                                            key={link.id}
                                            link={link}
                                            index={index}
                                            onUpdate={handleUpdateLink}
                                            onDelete={handleDeleteLink}
                                        />
                                    ))}
                                    {provided.placeholder}
                                </List>
                            )}
                        </Droppable>
                    </Card>
                )}
            </Draggable>

            <LinkFormDialog
                open={createLinkDialogOpen}
                onSubmit={handleCreateLink}
                onClose={() => setCreateLinkDialogOpen(false)}
            />

            <LinkGroupFormDialog
                open={updateLinkGroupDialogOpen}
                linkGroup={linkGroup}
                onSubmit={handleUpdateLinkGroup}
                onClose={() => setUpdateLinkGroupDialogOpen(false)}
            />

            <ConfirmationDialog
                open={confirmDeleteDialogOpen}
                title="Delete link group"
                message={`You are about to delete the link group "${linkGroup.name}" and all links in it.`}
                onClose={() => setConfirmDeleteDialogOpen(false)}
                onConfirm={() => onDelete(linkGroup)}
                noUpdateAfterConfirm
            />
        </>
    );
};

export default LinkGroupCard;
